import { CustomToastComponent } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { TenderState } from "@/graphql/__generated__/graphql";
import { useGenerateOpenCalcHook } from "@/hooks";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { toast } from "react-toastify";
import { CircleLoader } from "@/components/shared/loaders";
import { useTenderPermissionsData } from "@/hooks/cache";

interface GenerateOpenCalculationsComponentProps {
  tenderState?: TenderState | null;
  tenderID?: string;
}

/**
 * Component for generating open calculations for a tender.
 * Handles the generation process, loading states, and success/warning toasts.
 *
 * @component
 * @param {TenderState | null | undefined} tenderState - The current state of the tender
 * @param {string | undefined} tenderID - The ID of the tender
 */
function GenerateOpenCalculationsComponent({ tenderState, tenderID }: GenerateOpenCalculationsComponentProps) {
  const { t } = useTranslation(["translation", "toasts"]);
  const { generateOpenCalc, loading } = useGenerateOpenCalcHook();
  const [showLoadingState, setShowLoadingState] = useState(false);
  const { canOpenCalcGenerator } = useTenderPermissionsData(Number(tenderID));

  const handleGenerateClick = async () => {
    setShowLoadingState(true);
    const result = await generateOpenCalc({ variables: { tenderId: Number(tenderID) } });

    if (!result.errors) {
      await new Promise(resolve => setTimeout(resolve, 20000));

      toast(CustomToastComponent, {
        toastId: "open-calc-generated-success",
        data: {
          title: t("toasts:toasts.opencalcsuccess"),
          content: t("toasts:toasts.opencalcsuccessdescription"),
          type: "success",
        }
      });

      toast(CustomToastComponent, {
        toastId: "open-calc-generated-spam",
        data: {
          title: t("toasts:toasts.opencalccheckspamtitle"),
          content: t("toasts:toasts.opencalccheckspamdescription"),
          type: "warning",
        },
        autoClose: 10000
      });
    }

    setShowLoadingState(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold">{t("edittender.options.generateopencalc.title")}</CardTitle>
        <CardDescription>{t("edittender.options.generateopencalc.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        {
          tenderState === TenderState.PendingBidDecision ? (
            <p className="text-sm text-orange-500">{t("edittender.options.generateopencalc.notcorrectstate")}</p>
          ) : (
            <Button
              variant="outline"
              onClick={handleGenerateClick}
              disabled={loading || showLoadingState || !canOpenCalcGenerator}
            >
              {loading || showLoadingState ? (
                <div className="flex items-center gap-2">
                  <CircleLoader
                    visible={true}
                    height={20}
                    width={20}
                    strokeWidth={4}
                    ariaLabel="circle-loader"
                  />
                  {t("edittender.options.generateopencalc.btngenerating")}
                </div>
              ) : (
                t("edittender.options.generateopencalc.btngenerate")
              )}
            </Button>
          )
        }
      </CardContent>
    </Card>
  );
}

export default GenerateOpenCalculationsComponent;
